/** Rig lookups bound to the current owners (BODY_RIGS, attachment anchors, eye sockets, bust frame). */
import { box, corners, expand, union } from '../../../shared/assets/hushStatic/geometry';
import { one } from '../../../shared/assets/hushStatic/validation';
import type { ShapeType } from '../AppearanceDefinitions';
import { attachmentAnchorsForPose } from '../parts/AttachmentGeometry';
import { BODY_RIGS, HUMANOID_GEOMETRY, bodyScaleFor, normalizeBodySize } from '../parts/HumanoidGeometry';
import { socketsFor } from '../parts/eyes/EyeStyles';
import { bustFrame } from '../parts/humanoid/NpcFinish';
import { apply } from './geometry';
import type { AccessoryPiece, HushAppearance, Matrix, VisualBounds } from './types';

export const NPC_POSES = ['idle', 'blink', 'walkA', 'walkB'] as const;
export type NpcPose = typeof NPC_POSES[number];

/** Same mapping as GhostRenderer's `headFitTransform`, as a matrix (classic-head space → this shape's head). */
export function headFitMatrix(shape: ShapeType): Matrix {
  const classic = BODY_RIGS.classic.head;
  const head = BODY_RIGS[shape].head;
  const sx = (head.outerRight - head.outerLeft) / (classic.outerRight - classic.outerLeft);
  const sy = 1 + (sx - 1) * 0.5;
  const cx = (head.outerLeft + head.outerRight) / 2;
  const classicCx = (classic.outerLeft + classic.outerRight) / 2;
  // GhostRenderer rounds these authored head-fit numbers to 3 decimal places.
  const f = (v: number) => Number(v.toFixed(3));
  const x = f(cx);
  const y = head.topY;
  const a = f(sx);
  const b = f(sy);
  return [a, 0, 0, b, x - a * classicCx, y - b * classic.topY];
}

export function rigFor(a: HushAppearance, pose: NpcPose = 'idle') {
  one(a.shape, ['classic', 'slim', 'round', 'wide'], 'body shape');
  one(pose, NPC_POSES, 'NPC pose');
  const shape = a.shape!;
  const rig = BODY_RIGS[shape];
  const hands = attachmentAnchorsForPose(pose, undefined, shape);
  const sockets = socketsFor(a.eyeStyle ?? { style: 'matched' });
  const dx = rig.faceOffsetX;
  const eyePoints = Object.values(sockets).flatMap((s) => [
    { x: s.x - s.rx - 4 + dx, y: s.y - s.ry - 4 }, { x: s.x + s.rx + 4 + dx, y: s.y + s.ry + 4 },
  ]);
  // The existing mouth sits below the sockets; include its friendly ink and a small margin.
  const face = union([box(eyePoints), { x: 45 + dx, y: 51, width: 45, height: 14 }]);
  const scale = bodyScaleFor(normalizeBodySize(a.bodySize));
  const pivot = HUMANOID_GEOMETRY.characterPivot;
  return {
    shape, rig, hands, headMatrix: headFitMatrix(shape), faceClearance: face, bodyScale: scale,
    bodyMatrix: [scale, 0, 0, scale, pivot.x * (1 - scale), pivot.y * (1 - scale)] as Matrix,
    origin: { x: 70, y: 114 }, referenceBodyBounds: { x: 0, y: 0, width: 140, height: 120 }, nativeBust: bustFrame(scale),
  };
}

/** Square crop covering the native bust plus every portrait-required piece (cap, held prop). */
export function portraitCropFor(a: HushAppearance, pieces: readonly AccessoryPiece[]): VisualBounds {
  const r = rigFor(a);
  const required = pieces.filter((p) => p.portraitRequired).map((p) => box(corners(p.paintBounds).map((q) => apply(r.bodyMatrix, q))));
  const b = expand(union([r.nativeBust, ...required]), 2);
  const size = Math.max(b.width, b.height);
  const cx = b.x + b.width / 2;
  const cy = b.y + b.height / 2;
  return { x: cx - size / 2, y: cy - size / 2, width: size, height: size };
}
